import { useNavigate } from "react-router-dom";
import { YMark, BrontoIntelligence } from "@/components/Brand";
import { useTheme } from "@/context/ThemeContext";
import { Seo } from "@/lib/useSeo";

/**
 * AuthLanding — web port of iOS `src/app/(auth)/landing.tsx`.
 *
 * Structure (matches the RN source line-for-line):
 *   SafeArea → hero (Y mark centered, fills flex-1)
 *          → actions column (paddingHorizontal 24, gap 12):
 *              • pill button — rainbow-filled  "Create an account"
 *              • pill button — rainbow-hairline "Log in"
 *              • legal footer (brand-color links, no underline)
 *          → Bronto Intelligence footer (mark + wordmark, stacked)
 *
 * Rainbow order from iOS theme.ts:  #FFD600 → #FF7A00 → #FF0069 → #D300C5 → #7638FA
 */

// Exact rainbow order from iOS `theme.ts` — do not change.
const BRONTO_RAINBOW =
  "linear-gradient(90deg, #FFD600 0%, #FF7A00 25%, #FF0069 50%, #D300C5 75%, #7638FA 100%)";

// Exact URLs from iOS `src/lib/legal.ts`.
const PRIVACY_URL = "https://brontoy.com/privacy";
const GUIDELINES_URL = "https://brontoy.com/guidelines";
const TERMS_URL = "https://brontoy.com/terms";

export default function AuthLanding() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  // Brand accent color for legal links, per iOS theme.ts.
  const brandLink = theme === "dark" ? "#9B6BFF" : "#7638FA";

  const go = (path) => navigate(path);

  return (
    <div
      className="min-h-[100dvh] w-full flex flex-col bg-background text-foreground"
      data-testid="welcome-screen"
    >
      <Seo
        title="Join BrontoY — Create an account or Log in"
        description="Create an account or log in to BrontoY, the social platform by Bronto Intelligence. Share moments, express views, and start real conversations."
        canonicalPath="/welcome"
      />
      {/* Hero — Y mark centered. iOS spec: min(width * 0.38, 160) with a bit
          more headroom on larger web viewports so it doesn't look tiny on
          desktop. Aspect ratio preserved via the Brand.jsx YMark component. */}
      <div className="flex-1 flex items-center justify-center px-6 pt-6">
        <div className="w-[38vw] max-w-[160px] sm:max-w-[190px] md:max-w-[210px] lg:max-w-[230px]">
          <YMark
            height={230}
            className="!w-full !h-auto max-w-full anim-fade-up"
            testId="welcome-y-mark"
          />
        </div>
      </div>

      {/* Actions — iOS spec: paddingHorizontal 24, gap 12. Buttons height 56 (h-14),
          full-round pills, iOS-style press-down feedback (active:scale-98). */}
      <div
        className="w-full max-w-[440px] mx-auto px-6 flex flex-col gap-3 anim-fade-up"
        style={{ animationDelay: "0.28s" }}
      >
        {/* Create an account — rainbow-filled */}
        <button
          type="button"
          onClick={() => go("/auth/options?mode=signup")}
          className="h-14 rounded-full flex items-center justify-center text-white text-[17px] font-bold transition-all active:scale-[0.98] active:opacity-90 hover:opacity-95"
          style={{ background: BRONTO_RAINBOW }}
          data-testid="welcome-signup"
        >
          Create an account
        </button>

        {/* Log in — rainbow hairline border pill */}
        <div
          className="h-14 rounded-full transition-all"
          style={{ background: BRONTO_RAINBOW, padding: "1.5px" }}
        >
          <button
            type="button"
            onClick={() => go("/auth/options?mode=login")}
            className="w-full h-full rounded-full flex items-center justify-center bg-background text-foreground text-[17px] font-bold transition-all active:scale-[0.98]"
            data-testid="welcome-login"
          >
            Log in
          </button>
        </div>

        {/* Legal footer — copy verbatim from iOS `LegalConsent.tsx`. Brand-colour
            links, weight 600, NO underline. */}
        <p
          className="text-center text-[13px] leading-[19px] text-muted-foreground"
          style={{ marginTop: 14 }}
          data-testid="welcome-legal-text"
        >
          By continuing, you agree to our{" "}
          <a
            href={PRIVACY_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold no-underline hover:opacity-80"
            style={{ color: brandLink }}
            data-testid="welcome-privacy-link"
          >
            Privacy Policy
          </a>
          ,{" "}
          <a
            href={GUIDELINES_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold no-underline hover:opacity-80"
            style={{ color: brandLink }}
            data-testid="welcome-guidelines-link"
          >
            Community Guidelines
          </a>
          , and{" "}
          <a
            href={TERMS_URL}
            target="_blank"
            rel="noreferrer"
            className="font-semibold no-underline hover:opacity-80"
            style={{ color: brandLink }}
            data-testid="welcome-terms-link"
          >
            Terms of Use
          </a>
          .
        </p>
      </div>

      {/* Bronto Intelligence footer — mark + wordmark stack.
          iOS spec: paddingTop 28, paddingBottom 10, markHeight 36, wordmarkHeight 15. */}
      <div className="flex flex-col items-center pt-7 pb-2.5 anim-fade-up" style={{ animationDelay: "0.5s" }}>
        <BrontoIntelligence markHeight={36} wordmarkHeight={15} />
      </div>
    </div>
  );
}
